import { type PayChangeRequest, type PerformanceReview } from '@kalos/kalos-rpc';

import { type StatusPillTone } from '../../../components/StatusPill';
import { payChangeStatusTone } from '../../ComponentsLibrary/PerformanceReviewView/payChangeModel/payChangeRules';
import { type ReviewRole } from './reviewAccess';
import { dateLabel, daysLate, isDateSet } from './reviewDates';

export type ReviewStep =
  | 'self'
  | 'manager'
  | 'payApproval'
  | 'oneOnOne'
  | 'readyToSend'
  | 'sent'
  | 'finalized';
export type StepOwner = 'employee' | 'manager' | 'payApprover' | 'hr';
export type MissingToSend = 'self' | 'manager' | 'pay' | 'oneOnOne';

export type ReviewProgress = {
  step: ReviewStep;
  owner: StepOwner | null;
  daysLate: number;
  selfDone: boolean;
  selfSkipped: boolean;
  managerDone: boolean;
  oneOnOneDone: boolean;
  payPending: boolean;
  /** Locked for a pay change, but no request is pending: approving or rejecting didn't unlock it. */
  staleLock: boolean;
  /** The most recently decided request, Approved or Rejected. */
  latestDecided?: PayChangeRequest;
  missingToSend: MissingToSend[];
};

type ReviewFields = Pick<
  PerformanceReview,
  | 'statusId'
  | 'scheduleDate'
  | 'selfReviewDate'
  | 'selfReviewSkipped'
  | 'managerReviewDate'
  | 'oneOnOneDate'
  | 'completedDate'
  | 'finalizeDate'
  | 'lockReview'
>;

const decidedAt = (request: PayChangeRequest) =>
  isDateSet(request.dateReviewed) ? request.dateReviewed : request.dateSubmitted;

// First match wins.
const currentStep = (state: {
  statusId: number;
  payPending: boolean;
  selfOpen: boolean;
  managerDone: boolean;
  oneOnOneMissing: boolean;
}): [ReviewStep, StepOwner | null] => {
  if (state.statusId === 6) return ['finalized', null];
  if (state.statusId === 5) return ['sent', 'hr'];
  if (state.payPending) return ['payApproval', 'payApprover'];
  if (state.selfOpen) return ['self', 'employee'];
  if (!state.managerDone) return ['manager', 'manager'];
  if (state.oneOnOneMissing) return ['oneOnOne', 'manager'];
  return ['readyToSend', 'manager'];
};

/**
 * Where a review is and whose turn it is, derived from its step dates and pay change requests, so
 * nothing has to write a step: approving a pay change elsewhere moves the review on by itself.
 * Statuses 1 to 3 are older rows and read like 4.
 */
export const nextStep = ({
  review,
  payChangeRequests,
  oneOnOneRequired,
  now,
}: {
  review: ReviewFields;
  payChangeRequests: PayChangeRequest[];
  oneOnOneRequired: boolean;
  now: Date;
}): ReviewProgress => {
  const selfDone = isDateSet(review.selfReviewDate);
  const selfSkipped = review.selfReviewSkipped;
  const managerDone = isDateSet(review.managerReviewDate);
  const oneOnOneDone = isDateSet(review.oneOnOneDate);
  const payPending = payChangeRequests.some((request) => request.requestStatus === 'Pending');
  const latestDecided = payChangeRequests
    .filter(
      (request) => request.requestStatus === 'Approved' || request.requestStatus === 'Rejected',
    )
    .sort((first, second) => decidedAt(second).localeCompare(decidedAt(first)))[0];
  const oneOnOneMissing = oneOnOneRequired && !oneOnOneDone;

  const missingToSend: MissingToSend[] = [];
  if (!selfDone && !selfSkipped) missingToSend.push('self');
  if (!managerDone) missingToSend.push('manager');
  if (payPending) missingToSend.push('pay');
  if (oneOnOneMissing) missingToSend.push('oneOnOne');

  const [step, owner] = currentStep({
    statusId: review.statusId,
    payPending,
    selfOpen: !selfDone && !selfSkipped,
    managerDone,
    oneOnOneMissing,
  });

  return {
    step,
    owner,
    daysLate: review.statusId < 5 ? daysLate(review.scheduleDate, now) : 0,
    selfDone,
    selfSkipped,
    managerDone,
    oneOnOneDone,
    payPending,
    staleLock: review.lockReview === 1 && !payPending,
    latestDecided,
    missingToSend,
  };
};

/** The progress a viewer may know about: the employee never sees pay, so theirs leaves it out. */
export const reviewProgress = ({
  review,
  payChangeRequests,
  roles,
  now,
}: {
  review: ReviewFields;
  payChangeRequests: PayChangeRequest[];
  roles: ReviewRole[];
  now: Date;
}) => {
  const isEmployee = roles.includes('employee');
  return nextStep({
    review: isEmployee ? { ...review, lockReview: 0 } : review,
    payChangeRequests: isEmployee ? [] : payChangeRequests,
    oneOnOneRequired: true,
    now,
  });
};

export const STEP_LABEL: Record<ReviewStep, string> = {
  self: 'Self review',
  manager: 'Manager review',
  payApproval: 'Pay approval',
  oneOnOne: '1:1 meeting',
  readyToSend: 'Ready to send',
  sent: 'Sent',
  finalized: 'Finalized',
};

export const STEP_TONE: Record<ReviewStep, StatusPillTone> = {
  self: 'info',
  manager: 'info',
  payApproval: 'warning',
  oneOnOne: 'info',
  readyToSend: 'info',
  sent: 'info',
  finalized: 'success',
};

export const payStatusTone = (status: string): StatusPillTone => {
  const tone = payChangeStatusTone(status);
  return tone === 'neutral' ? 'muted' : tone;
};

export type PrimaryKind =
  | 'submitSelf'
  | 'finishManager'
  | 'addOneOnOne'
  | 'send'
  | 'finalize'
  | 'openPayChanges';
export type PrimaryAction = { kind: PrimaryKind; label: string };

const rolePrimary = (
  role: ReviewRole,
  progress: ReviewProgress,
  employeeFirstName: string,
): PrimaryAction | null => {
  const sentOrFinal = progress.step === 'sent' || progress.step === 'finalized';
  if (role === 'hr') {
    return progress.step === 'finalized' ? null : { kind: 'finalize', label: 'Finalize review' };
  }
  if (role === 'payApprover') {
    return progress.step === 'payApproval'
      ? { kind: 'openPayChanges', label: 'Open Pay Change Requests' }
      : null;
  }
  if (role !== 'manager' || sentOrFinal) return null;
  if (!progress.selfDone && !progress.selfSkipped) return null;
  if (!progress.managerDone) return { kind: 'finishManager', label: 'Finish manager review' };
  if (progress.missingToSend.includes('oneOnOne')) {
    return { kind: 'addOneOnOne', label: 'Add 1:1 date' };
  }
  return { kind: 'send', label: `Send review to ${employeeFirstName}` };
};

const OWNER_ROLE: Record<StepOwner, ReviewRole> = {
  employee: 'employee',
  manager: 'manager',
  payApprover: 'payApprover',
  hr: 'hr',
};

/**
 * The one primary button for this viewer. The role that owns the step goes first; otherwise HR,
 * then the manager, then a pay approver. A leader has none.
 */
export const primaryAction = ({
  progress,
  roles,
  employeeFirstName,
}: {
  progress: ReviewProgress;
  roles: ReviewRole[];
  employeeFirstName: string;
}): PrimaryAction | null => {
  if (roles.includes('employee')) {
    return progress.step === 'self' ? { kind: 'submitSelf', label: 'Submit self review' } : null;
  }
  const order: ReviewRole[] = ['hr', 'manager', 'payApprover'];
  if (progress.owner) order.unshift(OWNER_ROLE[progress.owner]);
  for (const role of order) {
    if (!roles.includes(role)) continue;
    const primary = rolePrimary(role, progress, employeeFirstName);
    if (primary) return primary;
  }
  return null;
};

type Names = { employeeName: string; managerName: string };

const ownerName = (owner: StepOwner, { employeeName, managerName }: Names) =>
  owner === 'employee'
    ? employeeName
    : owner === 'manager'
      ? managerName
      : owner === 'payApprover'
        ? 'a pay approver'
        : 'HR';

/** "Waiting on you", "Waiting on Sam Lee", "Waiting on a pay approver"; nothing once finalized. */
export const waitingOnText = ({
  progress,
  roles,
  ...names
}: { progress: ReviewProgress; roles: ReviewRole[] } & Names) => {
  if (!progress.owner) return null;
  if (roles.includes(OWNER_ROLE[progress.owner])) return 'Waiting on you';
  if (progress.owner === 'hr') return 'Waiting on HR to finalize';
  return `Waiting on ${ownerName(progress.owner, names)}`;
};

/** What Send is still missing, named in order. Nothing when the review is ready. */
export const missingToSendText = ({
  progress,
  employeeFirstName,
  viewerIsManager,
}: {
  progress: ReviewProgress;
  employeeFirstName: string;
  viewerIsManager: boolean;
}) => {
  if (progress.missingToSend.length === 0) return null;
  const parts: Record<MissingToSend, string> = {
    self: `${employeeFirstName}'s self review`,
    manager: viewerIsManager ? 'your manager review' : 'the manager review',
    pay: 'pay approval',
    oneOnOne: 'the 1:1 date',
  };
  return `Still needed before sending: ${progress.missingToSend.map((item) => parts[item]).join(', ')}.`;
};

export const finalizeBlockedText = ({ progress, ...names }: { progress: ReviewProgress } & Names) =>
  `You can finalize once the review is sent. It's at ${STEP_LABEL[progress.step]}, waiting on ${
    progress.owner ? ownerName(progress.owner, names) : 'nobody'
  }.`;

/** The `?step=` values a link may carry, one per step a person acts on. */
export const STEP_PARAMS = ['self', 'manager', 'pay', 'one-on-one', 'send'] as const;
export type StepParam = (typeof STEP_PARAMS)[number];

export type ReviewSectionId = 'self-review' | 'manager-review' | 'pay-change' | 'review-details';

const PARAM_SECTION: Record<StepParam, ReviewSectionId> = {
  self: 'self-review',
  manager: 'manager-review',
  pay: 'pay-change',
  'one-on-one': 'review-details',
  send: 'review-details',
};

const STEP_SECTION: Record<ReviewStep, ReviewSectionId> = {
  self: 'self-review',
  manager: 'manager-review',
  payApproval: 'pay-change',
  oneOnOne: 'review-details',
  readyToSend: 'review-details',
  sent: 'manager-review',
  finalized: 'manager-review',
};

/**
 * The section a link opens. A missing step, or one this viewer can't see (pay, for the employee),
 * opens the current step instead, so a link never shows what the viewer may not.
 */
export const sectionForStep = ({
  param,
  progress,
  roles,
}: {
  param: StepParam | undefined;
  progress: ReviewProgress;
  roles: ReviewRole[];
}): ReviewSectionId => {
  const current = STEP_SECTION[progress.step];
  if (!param) return current;
  const section = PARAM_SECTION[param];
  if (section === 'pay-change' && roles.includes('employee')) return current;
  return section;
};

/** The self review's row in Review details: "Submitted Oct 3, 2026", "Skipped Oct 3, 2026" or "Not submitted yet". */
export const selfReviewStatusText = (
  review: Pick<PerformanceReview, 'selfReviewDate' | 'selfReviewSkippedDate'>,
  progress: Pick<ReviewProgress, 'selfDone' | 'selfSkipped'>,
) => {
  if (progress.selfDone) return `Submitted ${dateLabel(review.selfReviewDate, '')}`;
  if (progress.selfSkipped) return `Skipped ${dateLabel(review.selfReviewSkippedDate, '')}`.trim();
  return 'Not submitted yet';
};

/** The manager review's row in Review details: "Finished Oct 3, 2026" or "Not finished yet". */
export const managerReviewStatusText = (
  review: Pick<PerformanceReview, 'managerReviewDate'>,
  progress: Pick<ReviewProgress, 'managerDone'>,
) =>
  progress.managerDone ? `Finished ${dateLabel(review.managerReviewDate, '')}` : 'Not finished yet';
